import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { errorWarnPropType } from './error-warn-propType';
import { DataTable } from './data-table';
import { startCostReview, retryValidation } from '../../../../store/bulk/purchase/bulk.purchase.actions';
import { BulkErrorsAndWarningsService } from '../../../../services/bulk-errors-and-warnings-service/';
import { removeFulfillmentPlansFromBatch } from '../../../../store/batches/details/batch.details.actions';
import { getBatchRows } from '../../../../store/batches/grid/batches.grid.actions';
import { useIsQuickshipEnabled, useQuickshipShowWarnings, useQuickshipShowErrors } from '../../../../hooks/use-user-setting';
import { messages } from './messages';
import { noop } from 'ui-components/default-props/noop';

import style from './style.module.less';
import sharedStyle from '../style.module.less';
import classnames from 'classnames';
import {
  shouldShowAmazonTermsError,
  showAmazonTermsErrorNotification,
} from '../../../../pages/amazon-buy-shipping-terms/error-notification-helper';

const ERROR = 'error';
const WARNING = 'warning';

const getSelectedIds = (localErrorOrWarnCollection) => localErrorOrWarnCollection.filter((item) => item.isSelected).map((item) => item.id);

const continueClick = (
  localErrorOrWarnCollection,
  included,
  excluded,
  errored,
  andPrint,
  isUserOnAnOpenBatch,
  dispatch,
  batchId,
  onEndBatchesErrorsAndWarnings,
) => {
  const selectedIds = getSelectedIds(localErrorOrWarnCollection);
  const newIncluded = included.filter((id) => !selectedIds.includes(id));
  const newExcluded = [...excluded, ...selectedIds];
  if (isUserOnAnOpenBatch) {
    if (newExcluded.length) {
      dispatch(
        removeFulfillmentPlansFromBatch({
          fulfillmentPlanSetId: batchId,
          fulfillmentPlanIds: newExcluded,
        }),
      );
    }
    onEndBatchesErrorsAndWarnings(newExcluded);
    dispatch(getBatchRows());
    return;
  }
  dispatch(startCostReview(newIncluded, newExcluded, errored, andPrint));
};

export const LabelBatchErrorWarn = ({
  batchId,
  totalLabels = 0,
  modalFrameworkMethods,
  job,
  onEndBatchesErrorsAndWarnings = noop,
  onRemoveRow = noop,
}) => {
  const dispatch = useDispatch();
  const isUserOnAnOpenBatch = !!batchId;
  const [localErrorOrWarnCollection, setErrorOrWarnCollection] = useState([]);

  // TODO: make this compatible with batches. This data doesn't exist in this form in batches version of validation
  const { included = [], excluded = [], errored = [], andPrint = false } = job || {};

  const handleContinueClick = useCallback(() => {
    return continueClick(
      localErrorOrWarnCollection,
      included,
      excluded,
      errored,
      andPrint,
      isUserOnAnOpenBatch,
      dispatch,
      batchId,
      onEndBatchesErrorsAndWarnings,
    );
  }, [
    localErrorOrWarnCollection,
    included,
    excluded,
    errored,
    andPrint,
    isUserOnAnOpenBatch,
    dispatch,
    batchId,
    onEndBatchesErrorsAndWarnings,
  ]);

  const getSelectedFulfillmentPlanIds = useCallback(
    () => localErrorOrWarnCollection.filter((item) => item.isSelected).map((item) => item.id),
    [localErrorOrWarnCollection],
  );
  const isQuickshipEnabled = useIsQuickshipEnabled();
  const shouldQuickshipShowWarnings = useQuickshipShowWarnings() || !isQuickshipEnabled;
  const shouldQuickshipShowErrors = useQuickshipShowErrors() || !isQuickshipEnabled;

  useEffect(() => {
    const {
      errorsAndWarnings = [],
      forceErrorStep = false,
      errored: _errored = [],
      excluded: _excluded = [],
      included: _included = [],
    } = job || {};
    const filteredWarnings =
      shouldQuickshipShowWarnings || forceErrorStep
        ? errorsAndWarnings
        : errorsAndWarnings.filter((errOrWarn) => errOrWarn.type !== WARNING);
    const filteredErrorsAndWarnings =
      shouldQuickshipShowErrors || forceErrorStep ? filteredWarnings : filteredWarnings.filter((errOrWarn) => errOrWarn.type !== ERROR);
    const groupedErrorsAndWarnings = BulkErrorsAndWarningsService.groupErrorsAndWarningsByFulfillmentPlanAndType(filteredErrorsAndWarnings);
    if (!filteredErrorsAndWarnings.length) {
      continueClick([], _included, _excluded, _errored, andPrint, isUserOnAnOpenBatch, dispatch, batchId, onEndBatchesErrorsAndWarnings);
      return;
    }
    setErrorOrWarnCollection(groupedErrorsAndWarnings);
  }, [
    job,
    setErrorOrWarnCollection,
    shouldQuickshipShowErrors,
    shouldQuickshipShowWarnings,
    andPrint,
    batchId,
    isUserOnAnOpenBatch,
    dispatch,
    onEndBatchesErrorsAndWarnings,
  ]);

  const setRowSelected = useCallback(
    (isSelected, row) => {
      const { id } = row;
      setErrorOrWarnCollection(
        localErrorOrWarnCollection.map((item) => ({
          ...item,
          isSelected: item.id === id ? isSelected : item.isSelected,
        })),
      );
    },
    [localErrorOrWarnCollection, setErrorOrWarnCollection],
  );

  const removeItemsForAnOpenBatch = useCallback(
    (targetedFulfillmentPlanIds) => {
      onRemoveRow(targetedFulfillmentPlanIds);
    },
    [onRemoveRow],
  );

  const removeSelectedRows = useCallback(() => {
    const selectedIds = getSelectedFulfillmentPlanIds();
    if (isUserOnAnOpenBatch) {
      removeItemsForAnOpenBatch(selectedIds);
      return;
    }
    const newIncluded = included.filter((id) => !selectedIds.includes(id));
    dispatch(retryValidation(newIncluded));
  }, [getSelectedFulfillmentPlanIds, isUserOnAnOpenBatch, included, dispatch, removeItemsForAnOpenBatch]);

  const removeSingleRow = useCallback(
    (targetedFulfillmentPlanIds) => {
      if (isUserOnAnOpenBatch) {
        removeItemsForAnOpenBatch(targetedFulfillmentPlanIds);
        return;
      }
      const newIncluded = included.filter((id) => !targetedFulfillmentPlanIds.includes(id));
      dispatch(retryValidation(newIncluded));
    },
    [dispatch, included, isUserOnAnOpenBatch, removeItemsForAnOpenBatch],
  );

  const areAllErrorsSelected = useCallback(() => {
    return !localErrorOrWarnCollection.some((item) => (item.error || item.canPurchaseLabel === false) && !item.isSelected);
  }, [localErrorOrWarnCollection]);

  const toggleAllChecked = useCallback(() => {
    const allSelected = getSelectedFulfillmentPlanIds().length === localErrorOrWarnCollection.length;
    if (allSelected) {
      setErrorOrWarnCollection(
        localErrorOrWarnCollection.map((item) => ({
          ...item,
          isSelected: false,
        })),
      );
      return;
    }
    setErrorOrWarnCollection(localErrorOrWarnCollection.map((item) => ({ ...item, isSelected: true })));
  }, [getSelectedFulfillmentPlanIds, localErrorOrWarnCollection]);

  const renderContinue = (shouldDisableContinue, allErrorsSelected, onlyWarningsDisplayed) => {
    if (allErrorsSelected && !onlyWarningsDisplayed) {
      return (
        <Button disabled={shouldDisableContinue} onClick={handleContinueClick} theme='primary'>
          <FormattedMessage {...messages.removeAndContinue} />
        </Button>
      );
    }
    return (
      <Button disabled={shouldDisableContinue} onClick={handleContinueClick} theme='primary'>
        <FormattedMessage {...messages.continue} />
      </Button>
    );
  };

  const { closeModal } = modalFrameworkMethods;
  const allErrorsSelected = areAllErrorsSelected();
  const hasErrors = localErrorOrWarnCollection.filter((error) => error.type === 'error').length > 0;
  const hasWarnings = localErrorOrWarnCollection.filter((warning) => warning.type === 'warning').length > 0;
  const allRemoved = !localErrorOrWarnCollection.length;
  const shouldDisableContinue = !allErrorsSelected;
  const hasCannotPurchaseLabel = localErrorOrWarnCollection.filter((item) => item.canPurchaseLabel === false).length > 0;
  const onlyWarningsDisplayed = hasWarnings && !hasErrors && !hasCannotPurchaseLabel;

  const triggeredAmazonTerms = useRef(false);
  useEffect(() => {
    if (triggeredAmazonTerms.current) {
      return;
    }
    const errorMessages = localErrorOrWarnCollection.map((obj) => obj.messages).flat();
    const shouldShowAmazonError = shouldShowAmazonTermsError(errorMessages);
    if (shouldShowAmazonError) {
      triggeredAmazonTerms.current = true;
      showAmazonTermsErrorNotification();
    }
  }, [localErrorOrWarnCollection]);

  return (
    <React.Fragment>
      <ModalHeader className={sharedStyle['batch-processing-modal-header']} closeModal={closeModal}>
        <FormattedMessage {...messages.errorsAndWarningsTitle} />
      </ModalHeader>
      <ModalBody className={classnames(sharedStyle['batch-processing-modal-body'], style['error-warn-modal-body'])}>
        <div className={style['error-warn-header']}>
          <div className={style.instructions}>
            <FormattedMessage {...messages.instructions} />
          </div>
        </div>
        <div className={style['error-warn-collection']}>
          <Button disabled={!getSelectedFulfillmentPlanIds().length} onClick={removeSelectedRows} theme='tertiary'>
            <FormattedMessage {...messages.bulkRemove} />
          </Button>
          <DataTable
            allRemoved={allRemoved}
            batchId={batchId}
            closeModal={closeModal}
            errorOrWarnCollection={localErrorOrWarnCollection}
            hasToggleAllChecked={getSelectedFulfillmentPlanIds().length === localErrorOrWarnCollection.length}
            onRemoveRow={removeSingleRow}
            onRowSelected={setRowSelected}
            onToggleAllChecked={toggleAllChecked}
            totalLabels={totalLabels}
          />
        </div>
      </ModalBody>
      <ModalFooter className={style['error-warn-footer']}>
        <Button className={style['error-warn-cancel-button']} onClick={closeModal} theme='unstyled'>
          <FormattedMessage {...messages.cancel} />
        </Button>
        {renderContinue(shouldDisableContinue, allErrorsSelected, onlyWarningsDisplayed)}
      </ModalFooter>
    </React.Fragment>
  );
};

LabelBatchErrorWarn.propTypes = {
  batchId: PropTypes.string,
  totalLabels: PropTypes.number,
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  job: PropTypes.shape({
    errorsAndWarnings: errorWarnPropType,
    included: PropTypes.arrayOf(PropTypes.string),
    excluded: PropTypes.arrayOf(PropTypes.string),
    errored: PropTypes.arrayOf(PropTypes.string),
    andPrint: PropTypes.bool,
    forceErrorStep: PropTypes.bool,
  }).isRequired,
  onEndBatchesErrorsAndWarnings: PropTypes.func,
  onRemoveRow: PropTypes.func,
};
